import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GalleryHorizontalEnd, ChevronLeft, ChevronRight, Download, FileText, Copy, Sparkles, AlertTriangle, RefreshCw, Undo2, Trash2, Plus, Pencil, ArrowLeftRight, X, ImagePlus, ListChecks, ListOrdered, Lightbulb, Package } from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "../components/kit";
import { ProductLinkImport } from "../components/ProductLinkImport";
import api, { formatApiErrorDetail } from "../lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgUrl = (id, i, fmt, download = false, vid = "", bust = 0) =>
  `${API_BASE}/carousels/image/${id}/${i}/${fmt}?download=${download ? 1 : 0}${vid ? `&v=${vid}` : ""}&t=${vid}${bust ? `-${bust}` : ""}`;

const ARCHETYPES = [
  { id: "listicle", label: "Educational listicle", desc: '"5 mistakes that…" — one item per slide', icon: ListChecks },
  { id: "steps", label: "Step guide", desc: '"How to X in 4 steps" — giant step numbers', icon: ListOrdered },
  { id: "myth", label: "Myth / case study", desc: "Tension → truth → proof", icon: Lightbulb },
  { id: "product", label: "Product showcase", desc: "Product hero + one benefit per slide", icon: Package },
];
const STYLES = [
  { id: "typographic", label: "Typographic", desc: "Bold type on brand colors — free" },
  { id: "product", label: "Product", desc: "Your photo as background — free" },
  { id: "scene", label: "Scene", desc: "AI backgrounds — ⅓ creative set" },
];

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase">{children}</p>
);

const Segmented = ({ options, value, onSelect, testPrefix, size = "md" }) => (
  <div className={`inline-flex rounded-xl border border-[#EAE5DB] bg-white overflow-hidden ${size === "sm" ? "" : ""}`}>
    {options.map((o, i) => (
      <button
        key={o.id}
        onClick={() => onSelect(o.id)}
        title={o.desc}
        className={`${size === "sm" ? "px-3 py-1.5 text-[11px]" : "px-4 py-2 text-[12.5px]"} font-bold transition-colors ${i > 0 ? "border-l border-[#EAE5DB]" : ""} ${value === o.id ? "bg-[#181621] text-white" : "bg-white text-[#54505B] hover:bg-[#FBFAF7]"}`}
        data-testid={`${testPrefix}-${o.id}`}
      >
        {o.label}{o.id === "scene" ? " · ⅓" : ""}
      </button>
    ))}
  </div>
);

const SlideEditor = ({ slide, idx, busy, onSave, onClose }) => {
  const [f, setF] = useState({ kicker: slide.kicker || "", title: slide.title || "", body: (slide.body || []).join("\n"), handle: slide.handle || "" });
  return (
    <div className="rounded-xl border border-[#EAE5DB] bg-[#FAF8F3] p-4 mt-3" data-testid="slide-editor">
      <div className="flex items-center justify-between">
        <SectionLabel>Edit slide {idx + 1}</SectionLabel>
        <button onClick={onClose} className="text-[#9A9591] hover:text-[#181621]" data-testid="slide-editor-close"><X size={14} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <input className="field-input !py-2 !text-[13px]" placeholder="Kicker" value={f.kicker} onChange={(e) => setF({ ...f, kicker: e.target.value })} data-testid="slide-edit-kicker" />
        <input className="field-input !py-2 !text-[13px]" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="slide-edit-title" />
      </div>
      {slide.layout !== "cover" && (
        <textarea className="field-input !py-2 !text-[13px] mt-3 w-full" rows={3} placeholder="Body — one line per row" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="slide-edit-body" />
      )}
      {slide.layout === "cta" && (
        <input className="field-input !py-2 !text-[13px] mt-3" placeholder="@handle" value={f.handle} onChange={(e) => setF({ ...f, handle: e.target.value })} data-testid="slide-edit-handle" />
      )}
      <div className="flex justify-end mt-3">
        <CtaButton className="!py-2 !px-4 text-[13px]" disabled={busy} onClick={() => onSave({ kicker: f.kicker, title: f.title, body: f.body.split("\n").map((x) => x.trim()).filter(Boolean), handle: f.handle })} data-testid="slide-edit-save">{busy ? "Re-rendering…" : "Save & re-render"}</CtaButton>
      </div>
    </div>
  );
};

export default function CarouselMaker() {
  const location = useLocation();
  const prefill = location.state?.carouselPrefill || null;
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [fmt, setFmt] = useState("portrait");
  const [phase, setPhase] = useState("idle");
  const [stage, setStage] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [editing, setEditing] = useState(false);
  const [chatMsg, setChatMsg] = useState("");
  const [chatRemember, setChatRemember] = useState(false);
  const [chatSavedHint, setChatSavedHint] = useState(false);
  const [bust, setBust] = useState(0);
  const [f, setF] = useState({ topic: prefill?.topic || "", archetype: prefill?.archetype || "listicle", slide_count: 6, style: "typographic", handle: "" });
  const [usage, setUsage] = useState(null);
  const [photo, setPhoto] = useState(null); // {upload_id, preview}
  const [uploading, setUploading] = useState(false);
  const [mutBusy, setMutBusy] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const slotIdRef = useRef(prefill?.slotId || null);
  const pillarRef = useRef(prefill?.pillar || null);

  const stopPolling = () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };

  const load = async (keepId = null) => {
    const { data } = await api.get("/carousels");
    setList(data);
    const found = keepId ? data.find((c) => c.id === keepId) : null;
    setActive(found || data[0] || null);
    return data;
  };

  useEffect(() => {
    load().catch(() => {});
    api.get("/creative-studio/usage").then(({ data }) => setUsage(data)).catch(() => {});
    api.get("/carousels/status").then(({ data }) => {
      if (data.status === "pending") { setPhase("generating"); setStage(data.stage || ""); startPolling(); }
    }).catch(() => {});
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = (keepId = null) => {
    stopPolling();
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 120) { stopPolling(); setPhase("error"); setErrMsg("The build took too long. Try again."); return; }
      try {
        const { data } = await api.get("/carousels/status");
        if (data.status === "failed") {
          stopPolling(); setPhase("error"); setErrMsg(data.error || "The build failed. Try again.");
        } else if (data.status === "complete") {
          stopPolling();
          await load(keepId);
          setPhase("idle");
          setSlideIdx(0);
          toast("Carousel ready.", { description: "Swipe through, edit, export." });
        } else { setStage(data.stage || ""); }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large — keep it under 8 MB.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/creative-studio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto({ upload_id: data.upload_id, preview: URL.createObjectURL(file) });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed. Try a smaller image.");
    } finally {
      setUploading(false);
    }
  };

  const generate = async () => {
    setErrMsg("");
    setPhase("generating");
    setStage("Writing the narrative…");
    try {
      const { data } = await api.post("/carousels/generate", {
        topic: f.topic.trim(), archetype: f.archetype, slide_count: f.slide_count, style: f.style,
        upload_id: photo?.upload_id || null,
        handle: f.handle.trim() || null, pillar: pillarRef.current, calendar_slot_id: slotIdRef.current,
      });
      startPolling(data.id);
    } catch (err) {
      setPhase("idle");
      if (err.response?.status === 402) { window.dispatchEvent(new CustomEvent("agenzee:upgrade")); return; }
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const mutate = async (fn, successMsg, key = "mut") => {
    if (mutBusy) return;
    setMutBusy(key);
    try {
      const { data } = await fn();
      setEditing(false);
      setActive((a) => ({ ...a, slides: data.slides ?? a.slides }));
      await load(active.id);
      setBust(Date.now());
      if (successMsg) toast(successMsg);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setMutBusy(null);
    }
  };

  const refine = () => {
    const t = chatMsg.trim();
    if (t.length < 2) return;
    setChatMsg("");
    setActive((a) => ({ ...a, refine_messages: [...(a.refine_messages || []), { role: "user", content: t }] }));
    setPhase("generating");
    setStage("Reading your notes…");
    api.post(`/carousels/${active.id}/refine`, { message: t, remember_instruction: !!chatRemember })
      .then(() => {
        if (chatRemember) {
          setChatSavedHint(true);
          setTimeout(() => setChatSavedHint(false), 3500);
          setChatRemember(false);
        }
        startPolling(active.id);
      })
      .catch((err) => { setPhase("idle"); load(active.id); toast.error(formatApiErrorDetail(err.response?.data?.detail)); });
  };

  const copyCaption = () => {
    navigator.clipboard.writeText(`${active.caption}\n\n${(active.hashtags || []).map((h) => `#${h}`).join(" ")}`)
      .then(() => toast("Caption copied."))
      .catch(() => toast.error("Copy failed — copy it manually."));
  };

  const slides = active?.slides || [];
  const slide = slides[slideIdx];
  const userMsgs = (active?.refine_messages || []).filter((m) => m.role === "user").length;
  const versionIdx = active ? (active.versions || []).findIndex((v) => v.vid === active.active_vid) : 0;
  const setsLeft = usage ? Math.round((usage.limit - usage.used) * 10) / 10 : null;

  return (
    <div className="p-6 sm:p-10 max-w-5xl" data-testid="carousel-maker-page">
      <MonoLabel>Organic Studio · Carousel Maker</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        Carousels that people actually swipe through
      </h1>
      {usage && (
        <div className="mt-5 max-w-sm" data-testid="carousel-usage-counter">
          <div className="h-1.5 rounded-full bg-[#F3EFE7] overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${usage.limit ? Math.min(100, (usage.used / usage.limit) * 100) : 0}%`,
                background: setsLeft < 2 ? "var(--amber-fg)" : "linear-gradient(90deg, #E0603A, #0B67FF)",
              }}
            />
          </div>
          <p className={`text-[11.5px] font-semibold mt-1.5 tabular-nums ${setsLeft < 2 ? "text-[#96691E]" : "text-[#54505B]"}`}>
            {setsLeft} of {usage.limit} creative sets left · each carousel uses ⅓
          </p>
        </div>
      )}

      {/* Intake */}
      <div className="card-soft mt-8 p-6 sm:p-7" data-testid="carousel-form">
        <div className="grid sm:grid-cols-[1fr_140px] gap-3">
          <div>
            <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">Topic / idea</label>
            <input className="field-input" placeholder='e.g. "5 errori che le PMI fanno con i bandi"' value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} data-testid="carousel-topic-input" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">Slides</label>
            <select className="field-input" value={f.slide_count} onChange={(e) => setF({ ...f, slide_count: Number(e.target.value) })} data-testid="carousel-count-select">
              {[5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-4 gap-2.5 mt-4">
          {ARCHETYPES.map((a) => {
            const sel = f.archetype === a.id;
            return (
              <button key={a.id} onClick={() => setF({ ...f, archetype: a.id })}
                className={`rounded-xl border p-3 text-left transition-colors ${sel ? "border-[#181621] bg-white shadow-sm" : "border-[#EAE5DB] bg-white/60 hover:border-[#9A9591]"}`}
                data-testid={`carousel-archetype-${a.id}`}>
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${sel ? "bg-[#181621]" : "bg-[#F3EFE7]"}`}>
                  <a.icon size={14} strokeWidth={1.8} className={sel ? "text-white" : "text-[#54505B]"} />
                </span>
                <p className="font-bold text-[12.5px] text-[#181621] mt-2">{a.label}</p>
                <p className="text-[11px] text-[#54505B] mt-0.5 leading-snug">{a.desc}</p>
              </button>
            );
          })}
        </div>
        <div className="mt-5 pt-5 border-t border-[#F3EFE7]" data-testid="carousel-product-block">
          <div className="flex flex-wrap items-start gap-5">
            <div className="w-[130px] shrink-0">
              <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">Product photo</label>
              {photo ? (
                <div className="relative" data-testid="carousel-photo-preview">
                  <img src={photo.preview} alt="product" className="w-full h-24 object-contain rounded-xl bg-[#F3EFE7] border border-[#EAE5DB]" />
                  <button onClick={() => setPhoto(null)} className="absolute -top-2 -right-2 bg-[#181621] text-white rounded-full p-1" data-testid="carousel-photo-remove-btn">
                    <X size={12} strokeWidth={2.2} />
                  </button>
                </div>
              ) : (
                <SecondaryButton className="!py-2 !px-3 text-[12px] w-full" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="carousel-photo-upload-btn">
                  <ImagePlus size={13} strokeWidth={1.8} /> {uploading ? "Uploading…" : "Upload"}
                </SecondaryButton>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} data-testid="carousel-photo-input" />
              <p className="text-[10.5px] text-[#9A9591] mt-1.5 leading-snug">Cut out and placed on the slides in the Product style.</p>
            </div>
            <div className="flex-1 min-w-[240px]">
              <ProductLinkImport
                target="creative"
                onApply={(p) => setF((prev) => ({ ...prev, topic: prev.topic.trim() ? prev.topic : [p.name, p.price_text].filter(Boolean).join(" — ").slice(0, 160) }))}
                onImage={(data, src) => setPhoto({ upload_id: data.upload_id, preview: src })}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-5 pt-5 border-t border-[#F3EFE7]">
          <div>
            <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">Style</label>
            <Segmented options={STYLES} value={f.style} onSelect={(id) => setF({ ...f, style: id })} testPrefix="carousel-style" />
          </div>
          <div className="self-end">
            <input className="field-input !py-2.5 !text-[13px] w-[160px]" placeholder="@handle (CTA slide)" value={f.handle} onChange={(e) => setF({ ...f, handle: e.target.value })} data-testid="carousel-handle-input" />
          </div>
          <div className="self-end ml-auto">
            <CtaButton onClick={generate} disabled={f.topic.trim().length < 4 || phase === "generating"} data-testid="carousel-generate-btn">
              <GalleryHorizontalEnd size={14} strokeWidth={2} /> Build carousel
            </CtaButton>
          </div>
        </div>
      </div>

      {phase === "generating" && (
        <div className="card-soft mt-6 p-8 flex flex-col items-center text-center" data-testid="carousel-generating-state">
          <div className="icon-tile animate-pulse" style={{ background: "var(--blue-bg)" }}>
            <GalleryHorizontalEnd size={18} strokeWidth={1.8} style={{ color: "var(--blue-fg)" }} />
          </div>
          <p className="font-bold text-[15px] text-[#181621] mt-4" data-testid="carousel-stage">{stage || "Building…"}</p>
          <p className="text-[12.5px] text-[#54505B] mt-1.5">Narrative first, then every slide is designed in your Brand Kit.</p>
        </div>
      )}
      {phase === "error" && (
        <div className="card-soft mt-6 p-6 flex items-center gap-3" data-testid="carousel-error-state">
          <AlertTriangle size={18} className="text-[#B4552D] shrink-0" />
          <p className="font-semibold text-[13.5px] text-[#181621] flex-1">{errMsg}</p>
          <SecondaryButton className="!py-1.5 !px-3 !text-[12px]" onClick={() => setPhase("idle")} data-testid="carousel-error-dismiss">OK</SecondaryButton>
        </div>
      )}

      {/* Deck */}
      {phase === "idle" && active && slides.length > 0 && (
        <div className="mt-10" data-testid="carousel-deck">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <p className="font-bold text-[14.5px] text-[#181621] truncate max-w-[320px]">{active.topic}</p>
              <span className="inline-flex items-center rounded-full border border-[#EAE5DB] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#54505B]">{active.archetype}</span>
              {active.pillar && <span className="inline-flex items-center rounded-full border border-[#BFDDB8] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#3E7B3E]">{active.pillar}</span>}
            </div>
            <Segmented
              size="sm"
              options={STYLES}
              value={active.style}
              testPrefix="deck-style"
              onSelect={(id) => {
                if (id === active.style) return;
                api.post(`/carousels/${active.id}/style`, { style: id }).then(({ data }) => {
                  if (data.status === "pending") { setPhase("generating"); setStage("Shooting the backgrounds…"); startPolling(active.id); }
                  else { load(active.id); toast("Style switched."); }
                }).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
              }}
            />
          </div>

          <div className="grid lg:grid-cols-[1fr_280px] gap-5 mt-4">
            <div className="card-soft overflow-hidden">
              <div className="p-3.5 flex items-center justify-between border-b border-[#F3EFE7] flex-wrap gap-2">
                <div className="inline-flex rounded-lg border border-[#EAE5DB] overflow-hidden">
                  {["portrait", "square"].map((ft, i) => (
                    <button key={ft} onClick={() => setFmt(ft)}
                      className={`px-3 py-1 text-[11px] font-bold tabular-nums transition-colors ${i > 0 ? "border-l border-[#EAE5DB]" : ""} ${fmt === ft ? "bg-[#181621] text-white" : "bg-white text-[#54505B] hover:bg-[#FBFAF7]"}`}
                      data-testid={`deck-fmt-${ft}`}>{ft === "portrait" ? "4:5" : "1:1"}</button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  {versionIdx > 0 && (
                    <button onClick={() => mutate(() => api.post(`/carousels/${active.id}/undo`), "Reverted to the previous version.", "undo")} disabled={mutBusy !== null} className="inline-flex items-center gap-1 text-[12px] font-bold text-[#54505B] hover:text-[#181621] disabled:opacity-50" data-testid="deck-undo-btn">
                      <Undo2 size={13} className={mutBusy === "undo" ? "animate-pulse" : ""} /> {mutBusy === "undo" ? "Reverting…" : "Undo"}
                    </button>
                  )}
                  <a href={`${API_BASE}/carousels/${active.id}/export/zip?fmt=${fmt}`} className="inline-flex items-center gap-1.5 rounded-lg border border-[#EAE5DB] bg-white px-2.5 py-1 text-[12px] font-bold text-[#54505B] hover:bg-[#181621] hover:text-white hover:border-[#181621] transition-colors" data-testid="deck-zip-btn"><Download size={12} /> PNG set</a>
                  <a href={`${API_BASE}/carousels/${active.id}/export/pdf`} className="inline-flex items-center gap-1.5 rounded-lg border border-[#EAE5DB] bg-white px-2.5 py-1 text-[12px] font-bold text-[#54505B] hover:bg-[#181621] hover:text-white hover:border-[#181621] transition-colors" data-testid="deck-pdf-btn"><FileText size={12} /> PDF</a>
                </div>
              </div>
              <div className="bg-[#F3EFE7] flex items-center justify-center py-4 relative">
                <button onClick={() => setSlideIdx(Math.max(0, slideIdx - 1))} disabled={slideIdx === 0} className="absolute left-3 z-10 rounded-full bg-white/90 p-2 shadow disabled:opacity-30" data-testid="deck-prev"><ChevronLeft size={16} /></button>
                <img key={`${active.active_vid}-${slideIdx}-${fmt}-${bust}`} src={imgUrl(active.id, slideIdx, fmt, false, active.active_vid, bust)} alt={`Slide ${slideIdx + 1}`} className="max-h-[540px] rounded-lg shadow" data-testid="deck-slide-image" />
                <button onClick={() => setSlideIdx(Math.min(slides.length - 1, slideIdx + 1))} disabled={slideIdx === slides.length - 1} className="absolute right-3 z-10 rounded-full bg-white/90 p-2 shadow disabled:opacity-30" data-testid="deck-next"><ChevronRight size={16} /></button>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-[11px] font-bold text-[#9A9591] tabular-nums uppercase tracking-[0.08em]">Slide {slideIdx + 1} of {slides.length} · {slide?.layout}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <SecondaryButton className="!py-1 !px-2.5 !text-[11.5px]" disabled={mutBusy !== null} onClick={() => setEditing(!editing)} data-testid="deck-edit-btn"><Pencil size={11} /> Edit copy</SecondaryButton>
                    <SecondaryButton className="!py-1 !px-2.5 !text-[11.5px]" disabled={mutBusy !== null} onClick={() => mutate(() => api.post(`/carousels/${active.id}/slides/${slideIdx}/regen`), "Slide copy regenerated.", "regen")} data-testid="deck-regen-slide-btn">
                      <RefreshCw size={11} className={mutBusy === "regen" ? "animate-spin" : ""} /> {mutBusy === "regen" ? "Regenerating…" : "Regen copy"}
                    </SecondaryButton>
                    {slideIdx > 0 && slideIdx < slides.length - 1 && (
                      <>
                        <SecondaryButton className="!py-1 !px-2.5 !text-[11.5px]" disabled={mutBusy !== null || slideIdx <= 1} onClick={() => {
                          const order = slides.map((_, i) => i);
                          [order[slideIdx - 1], order[slideIdx]] = [order[slideIdx], order[slideIdx - 1]];
                          mutate(() => api.post(`/carousels/${active.id}/slides/reorder`, { order }), "Reordered.", "move").then(() => setSlideIdx(slideIdx - 1));
                        }} data-testid="deck-move-left-btn"><ArrowLeftRight size={11} className={mutBusy === "move" ? "animate-pulse" : ""} /> {mutBusy === "move" ? "Moving…" : "Move ←"}</SecondaryButton>
                        <SecondaryButton className="!py-1 !px-2.5 !text-[11.5px]" disabled={mutBusy !== null} onClick={() => mutate(() => api.delete(`/carousels/${active.id}/slides/${slideIdx}`), "Slide removed.", "remove").then(() => setSlideIdx(Math.max(0, slideIdx - 1)))} data-testid="deck-remove-slide-btn">
                          {mutBusy === "remove" ? <RefreshCw size={11} className="animate-spin" /> : <Trash2 size={11} />}
                        </SecondaryButton>
                      </>
                    )}
                    {slides.length < 8 && slideIdx < slides.length - 1 && (
                      <SecondaryButton className="!py-1 !px-2.5 !text-[11.5px]" disabled={mutBusy !== null} onClick={() => mutate(() => api.post(`/carousels/${active.id}/slides/add`, { after: Math.max(0, slideIdx) }), "Slide added.", "add")} data-testid="deck-add-slide-btn">
                        {mutBusy === "add" ? <RefreshCw size={11} className="animate-spin" /> : <Plus size={11} />} {mutBusy === "add" ? "Writing the slide…" : "Add"}
                      </SecondaryButton>
                    )}
                  </div>
                </div>
                {editing && slide && <SlideEditor slide={slide} idx={slideIdx} busy={mutBusy === "edit"} onClose={() => setEditing(false)} onSave={(fields) => mutate(() => api.put(`/carousels/${active.id}/slides/${slideIdx}`, fields), "Slide updated.", "edit")} />}
              </div>
            </div>

            <div className="space-y-4">
              <div className="card-soft p-4">
                <div className="flex items-center justify-between">
                  <SectionLabel>Caption</SectionLabel>
                  <button onClick={copyCaption} className="text-[#54505B] hover:text-[#181621]" data-testid="deck-copy-caption"><Copy size={14} /></button>
                </div>
                <p className="text-[12.5px] text-[#181621] mt-2 whitespace-pre-line leading-snug" data-testid="deck-caption">{active.caption}</p>
                <p className="text-[11px] text-[#0B67FF] font-semibold mt-2">{(active.hashtags || []).map((h) => `#${h}`).join(" ")}</p>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {slides.map((s, i) => (
                  <button key={i} onClick={() => setSlideIdx(i)} className={`rounded-lg overflow-hidden border-2 transition-colors ${slideIdx === i ? "border-[#181621]" : "border-transparent hover:border-[#E7E2D6]"}`} data-testid={`deck-thumb-${i}`}>
                    <img src={imgUrl(active.id, i, "square", false, active.active_vid, bust)} alt="" className="w-full aspect-square object-cover" />
                  </button>
                ))}
              </div>
              {(active.versions || []).length > 0 && (
                <p className="text-[10.5px] font-semibold text-[#9A9591] text-center tabular-nums">Version {versionIdx + 1} of {active.versions.length} · last 3 kept</p>
              )}
            </div>
          </div>

          {/* Refine chat */}
          <div className="card-soft mt-5 p-5" data-testid="carousel-refine-chat">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <SectionLabel>Refine with AI</SectionLabel>
              <span className="text-[10.5px] font-semibold text-[#9A9591] tabular-nums" data-testid="carousel-refine-left">{10 - userMsgs} messages left</span>
            </div>
            {(active.refine_messages || []).length > 0 && (
              <div className="mt-3 space-y-2 max-h-52 overflow-y-auto pr-1">
                {active.refine_messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <p className={`rounded-2xl px-3.5 py-2 text-[12.5px] max-w-[85%] ${m.role === "user" ? "bg-[#181621] text-white" : "bg-[#FAF8F3] border border-[#F3EFE7] text-[#181621]"}`}>{m.content}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2.5 mt-3">
              <input className="field-input !py-2.5 flex-1" placeholder='"stronger hook", "slide 3 too dense", "warmer tone"' value={chatMsg} onChange={(e) => setChatMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); refine(); } }} disabled={userMsgs >= 10} data-testid="carousel-refine-input" />
              <CtaButton className="!py-2.5 !px-4 text-[13px]" onClick={refine} disabled={userMsgs >= 10 || chatMsg.trim().length < 2} data-testid="carousel-refine-send"><Sparkles size={13} /> Refine</CtaButton>
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
              <label className="inline-flex items-center gap-2 text-[11.5px] text-[#54505B] select-none cursor-pointer" data-testid="carousel-remember-label">
                <input type="checkbox" checked={chatRemember} onChange={(e) => setChatRemember(e.target.checked)}
                  className="w-3.5 h-3.5 accent-[#181621]" data-testid="carousel-remember-toggle" />
                Remember this instruction for future outputs
              </label>
              {chatSavedHint && (
                <span className="text-[11px] text-[#181621] font-semibold" data-testid="carousel-remember-saved">
                  Saved to your brand memory.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History */}
      {list.length > 1 && phase === "idle" && (
        <div className="mt-10" data-testid="carousel-history">
          <SectionLabel>Previous carousels</SectionLabel>
          <div className="flex gap-3 mt-3 overflow-x-auto pb-2">
            {list.map((c) => (
              <button key={c.id} onClick={() => { setActive(c); setSlideIdx(0); }} className={`shrink-0 rounded-xl border overflow-hidden w-[100px] bg-white transition-colors ${active?.id === c.id ? "border-[#181621]" : "border-[#EAE5DB] hover:border-[#9A9591]"}`} data-testid={`carousel-history-${c.id}`}>
                <img src={imgUrl(c.id, 0, "square", false, c.active_vid)} alt="" className="w-full aspect-square object-cover" />
                <p className="text-[10px] font-semibold text-[#54505B] p-1.5 truncate">{c.topic}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
